<template>
  <div v-if="computedSelectedOption">
    <header
      class="flex items-center overflow-hidden text-xl sm:overflow-visible lg:text-4xl"
    >
      <span class="whitespace-nowrap font-normal leading-snug">
        {{ t(`same_${computedSelectedOption.gender.toLowerCase()}`) }} </span
      >&nbsp;
      <span class="select w-0 flex-auto font-normal leading-snug lg:flex-min">
        <select v-model="selectedOption" class="font-medium">
          <option
            class="font-normal"
            v-for="option in options.filter((option) => !option.disabled)"
            :key="option.value"
            :value="option.value"
          >
            {{ t(option.value) }}
          </option>
        </select>
      </span>
    </header>

    <div class="layout-normal mt-10" v-if="objects.length">
      <ul class="row">
        <li
          v-for="object in objects"
          :key="object.id"
          class="object-teaser-wrapper"
        >
          <object-teaser-component :object="object" />
        </li>
      </ul>
    </div>
  </div>
</template>

<i18n>
{
  "en": {
    "same_neutral": "Same",
    "same_masculine": "Same",
    "same_feminine": "Same",
    "same_neuter": "Same",
    "DATING": "Date",
    "PERSON": "Artists/Producer",
    "THEME": "Theme",
    "DISTRICT": "District",
    "CLASSIFICATION": "Classification",
    "CLOCK_DRIVE": "Power Source",
    "ICONCLASS": "Subject",
    "MATERIAL": "Material",
    "TECHNIQUE": "Technique",
    "PLACE": "Place",
    "COIN_MINT": "Mint",
    "COIN_TERRITORY": "Mint Territory"
  },
  "de": {
    "same_neutral": "Gleiche/r",
    "same_masculine": "Gleicher",
    "same_feminine": "Gleiche",
    "same_neuter": "Gleiches",
    "DATING": "Datierung",
    "PERSON": "Künstler:in/Hersteller:in",
    "THEME": "Thema",
    "DISTRICT": "Bezirk",
    "CLASSIFICATION": "Objektart",
    "CLOCK_DRIVE": "Antriebsart",
    "ICONCLASS": "Bildmotiv",
    "MATERIAL": "Material",
    "TECHNIQUE": "Technik",
    "PLACE": "Ort",
    "COIN_MINT": "Münzstätte",
    "COIN_TERRITORY": "Münzstand"
  }
}
</i18n>

<script lang="ts">
import {
  Ref,
  computed,
  defineComponent,
  getCurrentInstance,
  ref,
  watch,
  type PropType,
} from 'vue';
import { Dating } from '../@types/dating';
import Gender from '../@types/gender';
import { Person } from '../@types/person';
import { Vocabulary } from '../@types/vocabulary';
import { createApi } from '../utils/gql';
import getObjects from '../utils/objects';
import { dispatchWindowEvent, lang } from '../utils/utils';
import ObjectTeaserComponent from './ObjectTeaserComponent.vue';
import { useI18n } from 'vue-i18n';

interface Option {
  gender: Gender;
  value: string;
  index?: number;
  label: string;
  disabled: boolean;
}

function notEmpty<T>(value: T): value is NonNullable<typeof value> {
  return value !== null && value !== undefined;
}

export default defineComponent({
  components: {
    ObjectTeaserComponent,
  },
  props: {
    objectId: {
      type: String,
      required: true,
    },
    datings: {
      type: Array as PropType<Dating[]>,
      default: () => [],
    },
    people: {
      type: Array as PropType<Person[]>,
      default: () => [],
    },
    classifications: {
      type: Array as PropType<Vocabulary[]>,
      default: () => [],
    },
    materials: {
      type: Array as PropType<Vocabulary[]>,
      default: () => [],
    },
    techniques: {
      type: Array as PropType<Vocabulary[]>,
      default: () => [],
    },
    themes: {
      type: Array as PropType<Vocabulary[]>,
      default: () => [],
    },
    iconclasses: {
      type: Array as PropType<Vocabulary[]>,
      default: () => [],
    },
    coinMint: {
      type: Object as PropType<Vocabulary>,
      default: null,
    },
    coinTerritory: {
      type: Object as PropType<Vocabulary>,
      default: null,
    },
    clockDrive: {
      type: Object as PropType<Vocabulary>,
      default: null,
    },
  },

  setup(props) {
    const instance = getCurrentInstance();
    const { t } = useI18n();
    const randomNumber = Math.random();
    const objects = ref([]);
    const api = createApi();

    const options: Ref<Option[]> = ref(
      [
        // Do not show "Unknown" person
        props.people &&
        props.people.filter((person) => person.personId !== 'p1').length
          ? {
              gender: Gender.NEUTRAL,
              value: 'PERSON',
              index: Math.floor(randomNumber * props.people.length),
            }
          : null,
        props.datings && props.datings.length
          ? {
              gender: Gender.FEMININE,
              value: 'DATING',
              index: Math.floor(randomNumber * props.datings.length),
            }
          : null,
        props.classifications && props.classifications.length
          ? {
              gender: Gender.FEMININE,
              value: 'CLASSIFICATION',
              index: props.classifications.length - 1,
            }
          : null,
        props.materials && props.materials.length
          ? {
              gender: Gender.NEUTER,
              value: 'MATERIAL',
              index: Math.floor(randomNumber * props.materials.length),
            }
          : null,
        props.techniques && props.techniques.length
          ? {
              gender: Gender.FEMININE,
              value: 'TECHNIQUE',
              index: Math.floor(randomNumber * props.techniques.length),
            }
          : null,
        props.themes && props.themes.length
          ? {
              gender: Gender.NEUTER,
              value: 'THEME',
              index: Math.floor(randomNumber * props.themes.length),
            }
          : null,
        props.iconclasses && props.iconclasses.length
          ? {
              gender: Gender.NEUTER,
              value: 'ICONCLASS',
              index: Math.floor(randomNumber * props.iconclasses.length),
            }
          : null,
        props.coinMint
          ? {
              gender: Gender.MASCULINE,
              value: 'COIN_MINT',
            }
          : null,
        props.coinTerritory
          ? {
              gender: Gender.FEMININE,
              value: 'COIN_TERRITORY',
            }
          : null,
        props.clockDrive
          ? {
              gender: Gender.FEMININE,
              value: 'CLOCK_DRIVE',
            }
          : null,
      ]
        .filter(notEmpty)
        .map((option: Omit<Option, 'label' | 'disabled'>) => ({
          ...option,
          label: option ? (t(option.value) as string) : '',
        }))
    );

    const selectedOption = ref<string | null>(
      options.value.length ? options.value[0].value : null
    );

    const computedSelectedOption = computed(() => {
      const option: Option | undefined = options.value.find(
        (opt) => opt && opt.value === selectedOption.value
      );

      return option;
    });

    const filter = computed(() => {
      if (!computedSelectedOption.value) {
        return null;
      }
      return {
        people:
          selectedOption.value === 'PERSON' &&
          props.people &&
          notEmpty(computedSelectedOption.value.index)
            ? props.people[computedSelectedOption.value.index].personId
            : null,
        datings:
          selectedOption.value === 'DATING' &&
          props.datings &&
          notEmpty(computedSelectedOption.value.index)
            ? {
                from: props.datings[computedSelectedOption.value.index].from
                  ? props.datings[computedSelectedOption.value.index].from
                  : 0,
                to: props.datings[computedSelectedOption.value.index].to
                  ? props.datings[computedSelectedOption.value.index].to
                  : props.datings[computedSelectedOption.value.index].from
                    ? props.datings[computedSelectedOption.value.index].from
                    : new Date().getFullYear(),
              }
            : null,
        classifications:
          selectedOption.value === 'CLASSIFICATION' &&
          props.classifications &&
          notEmpty(computedSelectedOption.value.index)
            ? props.classifications[computedSelectedOption.value.index].id
            : null,
        materials:
          selectedOption.value === 'MATERIAL' &&
          props.materials &&
          notEmpty(computedSelectedOption.value.index)
            ? props.materials[computedSelectedOption.value.index].id
            : null,
        techniques:
          selectedOption.value === 'TECHNIQUE' &&
          props.techniques &&
          notEmpty(computedSelectedOption.value.index)
            ? props.techniques[computedSelectedOption.value.index].id
            : null,
        themes:
          selectedOption.value === 'THEME' &&
          props.themes &&
          notEmpty(computedSelectedOption.value.index)
            ? props.themes[computedSelectedOption.value.index].id
            : null,
        iconclasses:
          selectedOption.value === 'ICONCLASS' &&
          props.iconclasses &&
          notEmpty(computedSelectedOption.value.index)
            ? props.iconclasses[computedSelectedOption.value.index].id
            : null,
        coinMints:
          selectedOption.value === 'COIN_MINT' && props.coinMint
            ? props.coinMint.id
            : null,
        coinTerritories:
          selectedOption.value === 'COIN_TERRITORY' && props.coinTerritory
            ? props.coinTerritory.id
            : null,
        clockDrives:
          selectedOption.value === 'CLOCK_DRIVE' && props.clockDrive
            ? props.clockDrive.id
            : null,
      };
    });

    const fetchObjects = () => {
      if (filter.value) {
        getObjects(
          api,
          lang,
          filter.value,
          { pageSize: 5, skip: 0 },
          undefined,
          true
        ).then((result) => {
          if (result) {
            objects.value = result.objects.filter(
              (object: any) => object.id !== props.objectId
            );
            if (objects.value.length === 0) {
              if (computedSelectedOption.value && selectedOption.value) {
                const index = options.value.indexOf(
                  computedSelectedOption.value
                );
                // Disable option without objects.
                options.value[index].disabled = true;
                if (index < options.value.length - 1) {
                  selectedOption.value = options.value[index + 1].value;
                } else {
                  selectedOption.value = options.value[0].value;
                }
              }
            }
            instance?.proxy.$nextTick(() => {
              dispatchWindowEvent('matchheights');
            });
          }
        });
      }
    };

    watch(filter, fetchObjects, { immediate: true });

    return {
      objects,
      options,
      selectedOption,
      computedSelectedOption,
      t,
    };
  },
});
</script>
